import React, { useState } from 'react';
import { useLiveMatch } from '../hooks/useLiveMatch';
import { ConfirmModal } from './ConfirmModal';
import { SubstitutionModal } from './SubstitutionModal';
import { CaptainTransferModal } from './CaptainTransferModal';
import { VARReviewModal } from './VARReviewModal';
import { OperatorModeSelector } from './OperatorModeSelector';
import { LEDScoreboard } from './broadcast/LEDScoreboard';
import { SiliconeToggle } from './broadcast/SiliconeToggle';
import { AutoHideHeader } from './AutoHideHeader';
import { TimeControlsModal } from './broadcast/TimeControlsModal';
import { ScoreEditModal } from './broadcast/ScoreEditModal';
import { PlayerActionModal } from './broadcast-min/PlayerActionModal';
import { MinPlayerRow } from './broadcast-min/MinPlayerRow';
import { TeamLogo } from './TeamLogo';
import { useLogoStyle } from '../hooks/useLogoStyle';

// Connection Status Light
const StatusLight = ({ connected, label }) => (
  <div className="flex items-center gap-2">
    <div className={`w-3 h-3 rounded-full ${connected ? 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.8)]' : 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.8)]'}`} />
    <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
  </div>
);

// Compact Timer
const CompactTimer = ({ minute, second, onClick }) => {
  const formattedTime = `${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
  return (
    <div
      className={`relative bg-black rounded-lg px-3 py-1.5 shadow-[inset_0_2px_8px_rgba(0,0,0,0.8)] border border-gray-800 ${onClick ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`}
      onClick={onClick}
      title={onClick ? 'Time controls' : ''}
    >
      <div className="seven-segment text-xl font-bold text-gray-800 tabular-nums leading-none" aria-hidden="true">88:88</div>
      <div className="absolute inset-0 px-3 py-1.5 seven-segment text-xl font-bold text-amber-400 tabular-nums leading-none">{formattedTime}</div>
    </div>
  );
};

// Header
const MinHeader = ({ connected, joined, viewers, match, matchId }) => {
  const getStatusBadge = () => {
    if (!match) return null;
    const statusColors = {
      scheduled: 'bg-blue-900/50 text-blue-400',
      live: 'bg-green-900/50 text-green-400',
      completed: 'bg-gray-700 text-gray-300',
      cancelled: 'bg-red-900/50 text-red-400',
    };
    return (
      <div className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${statusColors[match.status] || statusColors.scheduled}`}>
        {match.status}
      </div>
    );
  };

  return (
    <div className="h-14 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b border-amber-600/30 flex items-center justify-between px-4">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-bold text-amber-400 tracking-wide">BROADCAST-MIN</h1>
        {getStatusBadge()}
      </div>
      <div className="flex items-center gap-4">
        <StatusLight connected={connected} label="WS" />
        <StatusLight connected={joined} label="Room" />
        <span className="text-xs text-gray-500">{viewers}v</span>
      </div>
      <OperatorModeSelector currentMode="broadcast-min" matchId={matchId} />
    </div>
  );
};

// Score Bar with timer, logos, scores, overlay controls
const ScoreBar = ({ m, match, onOpenTimeControls, onOpenScoreEdit, logoStyle }) => (
  <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b border-gray-700 px-4 py-2">
    <div className="flex items-center justify-center gap-4">
      {/* Home */}
      <div className="flex items-center gap-2">
        <TeamLogo team={match?.home_team} size="w-8 h-8" logoStyle={logoStyle} shape="circle" />
        <span className="text-sm font-bold text-white truncate max-w-[100px]">
          {match?.home_team?.name || 'HOME'}
        </span>
      </div>

      {/* Score */}
      <div className="flex items-center gap-2 cursor-pointer" onClick={onOpenScoreEdit} title="Edit score">
        <LEDScoreboard score={m.score.home} color="blue" compact={true} />
        <span className="text-gray-500 text-lg">-</span>
        <LEDScoreboard score={m.score.away} color="red" compact={true} />
      </div>

      {/* Timer */}
      <CompactTimer minute={m.clock.minute} second={m.clock.second} onClick={onOpenTimeControls} />

      {/* Away */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-bold text-white truncate max-w-[100px]">
          {match?.away_team?.name || 'AWAY'}
        </span>
        <TeamLogo team={match?.away_team} size="w-8 h-8" logoStyle={logoStyle} shape="circle" />
      </div>

      {/* Divider */}
      <div className="w-px h-6 bg-gray-700" />

      {/* Overlay Toggles - Translucent silicone style */}
      <div className="flex items-center gap-1.5">
        <SiliconeToggle color="blue" active={m.scoreboardVisible} onClick={m.toggleScoreboard}>Score</SiliconeToggle>
        <SiliconeToggle color="blue" active={m.formationsVisible} onClick={m.toggleFormations}>Form</SiliconeToggle>
        <SiliconeToggle color="blue" active={m.logoVisible} onClick={m.toggleLogo}>Logo</SiliconeToggle>
      </div>
    </div>
  </div>
);

// Team Column
const TeamColumn = ({ players, label }) => {
  if (!players || players.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">No lineup</div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-3 py-1.5 border-b border-gray-700">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{label}</span>
      </div>
      <div className="flex-1 overflow-y-auto px-1 py-1">
        {players.map((playerPos) => (
          <MinPlayerRow key={playerPos.id} playerPos={playerPos} onSelect={() => {}} variant="compact" />
        ))}
      </div>
    </div>
  );
};

// Event Log
const EventLogCompact = ({ events, setVarModal }) => {
  const recent = events.slice(-8);
  const icons = { goal: '\u26BD', penalty_goal: '\u26BD', own_goal: '\u26BD', yellow_card: '\uD83D\uDFE8', red_card: '\uD83D\uDFE5', substitution: '\uD83D\uDD04', var_review: '\uD83D\uDCF9' };
  const colors = { goal: 'text-green-400', penalty_goal: 'text-green-400', own_goal: 'text-green-400', yellow_card: 'text-yellow-400', red_card: 'text-red-400', substitution: 'text-blue-400', var_review: 'text-purple-400' };

  const canVAR = (event) => {
    return ['goal', 'penalty_goal', 'own_goal', 'penalty', 'red_card'].some(t =>
      event.event_type?.toLowerCase().includes(t)
    ) && !event.is_cancelled;
  };

  return (
    <div className="h-16 bg-gray-900 border-t border-gray-700 px-3 py-1.5 overflow-x-auto">
      <div className="flex items-center gap-2 h-full">
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest whitespace-nowrap">Events:</span>
        {recent.length > 0 ? recent.map((ev, i) => (
          <div key={ev.id || i} className="flex items-center gap-1 bg-gray-800/50 rounded px-2 py-1 whitespace-nowrap">
            <span className="text-sm">{icons[ev.event_type] || '\u2022'}</span>
            <span className={`text-xs font-medium ${colors[ev.event_type] || 'text-gray-400'}`}>
              {ev.minute}' {ev.description || ev.event_type}
            </span>
            {canVAR(ev) && (
              <button
                onClick={() => setVarModal({ isOpen: true, event: ev })}
                className="ml-1 px-1.5 py-0.5 bg-purple-900/50 text-purple-400 text-[10px] font-bold rounded"
              >
                VAR
              </button>
            )}
          </div>
        )) : (
          <span className="text-xs text-gray-600">No events</span>
        )}
      </div>
    </div>
  );
};

// Connection Screen
const ConnectionScreen = () => (
  <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
    <div className="text-center">
      <div className="text-3xl font-bold text-amber-400 mb-4">BROADCAST-MIN</div>
      <div className="flex items-center justify-center gap-3">
        <div className="w-4 h-4 rounded-full bg-amber-500 animate-pulse shadow-[0_0_20px_rgba(245,158,11,0.8)]" />
        <div className="text-lg text-gray-300">CONNECTING...</div>
      </div>
    </div>
  </div>
);

// Main Component
export const LiveMatchViewBroadcastMin = ({ matchId }) => {
  const m = useLiveMatch(matchId);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedTeamSide, setSelectedTeamSide] = useState(null);
  const [timeControlsModal, setTimeControlsModal] = useState(false);
  const [scoreEditModal, setScoreEditModal] = useState(false);
  const { logoStyle } = useLogoStyle();

  if (!m.connected) {
    return <ConnectionScreen />;
  }

  const handlePlayerSelect = (playerPos, side) => {
    setSelectedPlayer(playerPos);
    setSelectedTeamSide(side);
  };

  const handlePlayerAction = (eventType, playerPos) => {
    if (eventType === 'substitution') {
      m.setSubstitutionModal({
        isOpen: true,
        team: selectedTeamSide,
        outgoingPlayer: playerPos,
      });
    } else {
      m.handleQuickEvent(eventType, playerPos);
    }
  };

  const handleScoreUpdate = (homeScore, awayScore) => {
    m.handleScoreUpdate(homeScore, awayScore);
  };

  // Team Column with onSelect wired
  const TeamColumnWithActions = ({ players, label, side }) => {
    if (!players || players.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">No lineup</div>
      );
    }

    const activePlayers = players.filter(p => p.is_starting);
    const activeCount = players.filter(p => p.eligibility?.eligible && !p.eligibility?.isExpelled && !p.eligibility?.isSubstituted).length;

    return (
      <div className="flex-1 flex flex-col min-h-0 bg-gray-800/30 rounded-lg border border-gray-700/50">
        <div className="px-3 py-2 border-b border-gray-700 flex items-center justify-between">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{label}</span>
          <span className="text-[10px] text-gray-500 font-mono">{activeCount}/{activePlayers.length}</span>
        </div>
        <div className="flex-1 overflow-y-auto px-1 py-1">
          {players.map((playerPos) => (
            <MinPlayerRow
              key={playerPos.id}
              playerPos={playerPos}
              onSelect={(pp) => handlePlayerSelect(pp, side)}
              variant="compact"
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col">
        <AutoHideHeader timeout={5000}>
          <MinHeader
            connected={m.connected}
            joined={m.joined}
            viewers={m.viewers}
            match={m.match}
            matchId={matchId}
          />
        </AutoHideHeader>

        <ScoreBar
          m={m}
          match={m.match}
          onOpenTimeControls={() => setTimeControlsModal(true)}
          onOpenScoreEdit={() => setScoreEditModal(true)}
          logoStyle={logoStyle}
        />

        {/* Two-column player lists */}
        <div className="flex-1 flex gap-2 p-2 overflow-hidden">
          <TeamColumnWithActions players={m.homeTeam} label="HOME" side="home" />
          <TeamColumnWithActions players={m.awayTeam} label="AWAY" side="away" />
        </div>

        <EventLogCompact events={m.events} setVarModal={m.setVarModal} />
      </div>

      {/* Player Action Modal */}
      <PlayerActionModal
        playerPos={selectedPlayer}
        onAction={handlePlayerAction}
        onClose={() => setSelectedPlayer(null)}
      />

      {/* Reuse existing modals */}
      <ConfirmModal
        isOpen={m.confirmModal.isOpen}
        title={m.confirmModal.title}
        message={m.confirmModal.message}
        onConfirm={m.confirmModal.onConfirm}
        onCancel={() => m.setConfirmModal({ isOpen: false })}
      />

      <SubstitutionModal
        isOpen={m.substitutionModal.isOpen}
        outgoingPlayer={m.substitutionModal.outgoingPlayer}
        benchPlayers={m.benchPlayersForSubstitution}
        onConfirm={m.handleSubstitutionConfirm}
        onCancel={() => m.setSubstitutionModal({ isOpen: false, outgoingPlayer: null })}
      />

      <CaptainTransferModal
        isOpen={m.captainTransferModal.isOpen}
        currentCaptain={m.captainTransferModal.currentCaptain}
        eligiblePlayers={m.eligiblePlayersForCaptain}
        onConfirm={(newCaptainId) => {
          m.handleCaptainTransfer(newCaptainId);
          m.setCaptainTransferModal({ isOpen: false });
        }}
        onCancel={() => m.setCaptainTransferModal({ isOpen: false })}
      />

      <VARReviewModal
        isOpen={m.varModal.isOpen}
        event={m.varModal.event}
        matchId={matchId}
        onClose={() => m.setVarModal({ isOpen: false, event: null })}
      />

      <TimeControlsModal
        isOpen={timeControlsModal}
        onClose={() => setTimeControlsModal(false)}
        clock={m.clock}
        onStartClock={m.handleStartClock}
        onStopClock={m.stopClock}
        onResetClock={m.resetClock}
        onChangeHalf={m.changeHalf}
        onSetInjuryTime={m.setInjuryTime}
        match={m.match}
        onStartMatch={m.handleStartMatch}
        onEndMatch={m.handleEndMatch}
        onCancelMatch={m.handleCancelMatch}
      />

      <ScoreEditModal
        isOpen={scoreEditModal}
        onClose={() => setScoreEditModal(false)}
        homeTeamName={m.match?.home_team?.name || 'HOME'}
        awayTeamName={m.match?.away_team?.name || 'AWAY'}
        currentHomeScore={m.score.home}
        currentAwayScore={m.score.away}
        onUpdateScore={handleScoreUpdate}
        events={m.events}
        createEvent={m.createEvent}
        deleteEvent={m.deleteEvent}
        eventTypes={m.eventTypes}
        clock={m.clock}
        homeLineup={m.homeTeam}
        awayLineup={m.awayTeam}
        matchId={matchId}
        match={m.match}
      />
    </>
  );
};
